"use client";

import React from "react";
import { Loader, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultationInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="36454" id="find-your-fit" ref={ref} className={`bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />
      
      <div className="max-w-3xl mx-auto relative z-10">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-4xl font-sans font-bold tracking-tight text-primary">
            Get a Free Consultation
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-lg">
            Take the first step toward restorative sleep and better health. Our board-certified specialists will review your symptoms and contact you to discuss your options.
          </p>
        </div>

        <div className="bg-card/50 backdrop-blur-md border border-border rounded-xl p-6 md:p-10 shadow-xl">
          <FormProvider formKey="find_fit_form_treatments" formId="find_fit_form_treatments" sectionName="find-your-fit" defaultValues={{
          name: "",
          email: "",
          phone: "",
          insurance_provider: "",
          symptoms: "",
          consent: false
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true
          },
          insurance_provider: {
            required: false
          },
          symptoms: {
            required: true
          },
          consent: {
            required: true
          }
        }} submitText="Get a Free Consultation" successMessage="Your inquiry has been received securely. A board-certified specialist will contact you shortly." errorMessage="There was an issue submitting your request. Please try again.">
            <ConsultationForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-chart-2/10 flex items-center justify-center mb-4">
          <Check className="size-8 text-chart-2" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-primary">Consultation Requested</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Full Name</FormLabel>
              <FormControl>
                <input {...field} placeholder="John Doe" className="flex h-12 w-full rounded-lg border border-input bg-background/50 px-4 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Email Address</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="john@example.com" className="flex h-12 w-full rounded-lg border border-input bg-background/50 px-4 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Phone Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="(555) 123-4567" className="flex h-12 w-full rounded-lg border border-input bg-background/50 px-4 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200" />
              </FormControl>
              <p className="text-[0.8rem] text-muted-foreground">Insurance Accepted | Board Certified Specialists</p>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="insurance_provider" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Insurance Provider</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-input h-12 rounded-lg">
                    <SelectValue placeholder="Select your provider" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["Medicare", "Blue Cross Blue Shield", "Aetna", "Cigna", "UnitedHealthcare", "Kaiser Permanente", "Other / No Insurance"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="symptoms" rules={{
      required: "Please describe your symptoms"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-medium">Symptoms & Health Concerns</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Describe your snoring, fatigue, gasping for air, or other sleep concerns..." className="flex min-h-[120px] w-full rounded-lg border border-input bg-background/50 px-4 py-3 text-base ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y transition-all duration-200" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="consent" rules={{
      required: "You must consent to proceed"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border border-border/50 p-4 bg-secondary/50">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="peer h-5 w-5 shrink-0 rounded border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground mt-0.5 accent-primary" />
            </FormControl>
            <div className="space-y-1 leading-tight">
              <FormLabel className="text-sm font-medium text-foreground/90">
                I consent to the collection of my health data for consultation purposes.
              </FormLabel>
              <p className="text-xs text-muted-foreground">
                HIPAA Compliant: Your data is secure, encrypted, and used only to facilitate your clinical care.
              </p>
            </div>
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[240px] mx-auto block h-14 text-lg font-bold bg-chart-2 text-white hover:opacity-90 rounded-lg shadow-md transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : "Get a Free Consultation"}
      </Button>
    </Form>;
}